import Axios from '../api/axios';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';

// Türkçe karakterler için harici font yükleyici
const FONT_NAME = 'DejaVuSans'; // public/fonts/DejaVuSans.ttf dosyası gerekir
let __fontLoaded = false;
async function ensureFont(doc) {
  if (__fontLoaded) return;
  try {
    // Font dosyasını public klasöründen çek
    const fontUrl = (process.env.PUBLIC_URL || '') + '/fonts/DejaVuSans.ttf';
    const resp = await fetch(fontUrl);
    if (!resp.ok) throw new Error('Font bulunamadı: ' + fontUrl);
    const buf = await resp.arrayBuffer();
    const uint = new Uint8Array(buf);
    // Güvenli base64 dönüşümü (chunk'lı) - bazı ortamlarda ampersand görünen bozuk mappingi engeller
    const chunkSize = 0x8000; // 32KB
    let binary = '';
    for (let i = 0; i < uint.length; i += chunkSize) {
      const chunk = uint.subarray(i, i + chunkSize);
      for (let j = 0; j < chunk.length; j++) binary += String.fromCharCode(chunk[j]);
    }
    const base64 = btoa(binary);
    doc.addFileToVFS(`${FONT_NAME}.ttf`, base64);
    doc.addFont(`${FONT_NAME}.ttf`, FONT_NAME, 'normal');
    __fontLoaded = true;
  } catch (e) {
    console.error('Türkçe font yükleme hatası, varsayılan font kullanılacak:', e.message);
  }
}

// HTML entity / &nbsp; vs temizleme + ampersand artefakt düzeltme
function sanitizeText(str) {
  if (!str || typeof str !== 'string') return str || '';
  let s = str
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&#39;/gi, "'")
    .replace(/&quot;/gi, '"');
  // Artefakt formu: &O&l&u& ...  -> O l u ...
  // Sadece tek harf veya rakam öncesindeki & kaldırılır (gerçek entity ile karışmaz)
  s = s.replace(/&(?![a-z]{2,};)([A-Za-z0-9ğüşöçıİ])/g, '$1');
  // Ardışık harfler bitişik gelmişse normal bırak; boşlukları koru.
  return s;
}

// Utility function to add a styled section header
function addSectionHeader(doc, title, y) {
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 15;
  doc.setFillColor(240, 240, 240);
  doc.rect(margin, y - 6, pageWidth - margin * 2, 10, 'F');
  doc.setFontSize(12);
  doc.setTextColor(40);
  doc.text(title, margin + 2, y);
  return y + 6;
}

function stampFooters(doc) {
  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    const pageHeight = doc.internal.pageSize.getHeight();
    const pageWidth = doc.internal.pageSize.getWidth();
    doc.setFontSize(8);
    doc.setTextColor(120);
    doc.text(`Sayfa ${i} / ${pageCount}`, pageWidth - 55, pageHeight - 12);
  }
}

async function fetchImageAsDataUrl(url) {
  try {
    const response = await Axios.get(url, { responseType: 'blob' });
    const blob = response.data;
    return await new Promise(resolve => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.readAsDataURL(blob);
    });
  } catch (err) {
    return null;
  }
}

export async function generateCariPDF(activeRow, glb, notify) {
  if (!activeRow || !activeRow.CAR_REF) {
    notify('Cari seçili değil', 'warning', 2000); return;
  }

  try {
    notify('PDF oluşturuluyor...', 'info', 1500);
    const base = process.env.REACT_APP_BACKEND_SERVER;
    const cid = glb.default.KULLANICI.KUL_DEF_FIR_REF;

    const requests = [
      { key: 'cari', url: `${base}CARI_KART/rec?id=${activeRow.CAR_REF}&cid=${cid}` },
      { key: 'satis', url: `${base}CARI_SATIS/list?CAR_REF=${activeRow.CAR_REF}` },
      { key: 'para', url: `${base}CARI_SATIS/paraList?CAR_REF=${activeRow.CAR_REF}` },
      { key: 'resim', url: `${base}fileops/carResList?CR_CAR_REF=${activeRow.CAR_REF}&filterType=all&cid=${cid}` }
    ];
    const settled = await Promise.allSettled(requests.map(r => Axios.get(r.url)));
    const pick = (idx) => settled[idx].status === 'fulfilled' ? settled[idx].value.data : [];
    const cari = pick(0) || {};
    const satisList = pick(1) || [];
    const paraList = pick(2) || [];
    const resimList = pick(3) || [];

    // Extract Anemnez fields directly from cari record
    const anemnezList = [];
    const anemnezFields = [
      ['Kronik Hastalıklar', cari.CAR_KRON_HAS],
      ['Kullandığı İlaçlar', cari.CAR_KUL_ILAC],
      ['Diğer Açıklama', cari.CAR_DIGER_ACK]
    ].filter(row => row[1] && row[1].toString().trim().length > 0);
    anemnezFields.forEach(f => anemnezList.push({ CA_ACK: `${f[0]}: ${f[1]}` }));

    // Arama listesi ayrı endpoint yoksa boş; ileri geliştirmede eklenecek
    const aramaList = [];

    const doc = new jsPDF({ unit: 'pt', format: 'a4' });
    await ensureFont(doc);
    doc.setFont(__fontLoaded ? FONT_NAME : undefined, 'normal');
    const margin = 40;

    // Title
    doc.setFontSize(18);
    doc.setTextColor(30);
    doc.text('CARI DOSYASI', doc.internal.pageSize.getWidth() / 2, margin, { align: 'center' });
    doc.setFontSize(10);
    doc.text(sanitizeText(`Oluşturma: ${new Date().toLocaleString('tr-TR')}`), doc.internal.pageSize.getWidth() - margin, margin, { align: 'right' });

    // Cari Bilgileri boxed
    let y = margin + 20;
    y = addSectionHeader(doc, 'CARI BILGILERI', y);
    doc.setFontSize(9);
    const cariInfo = [
      ['Ad Soyad', sanitizeText(cari.CAR_ADSOY || activeRow.CAR_ADSOY || '')],
      ['Cinsiyet', sanitizeText(cari.CAR_CINSIYET || activeRow.CAR_CINSIYET || '')],
      ['Telefon', sanitizeText(cari.CAR_CEP || cari.CAR_GSM || activeRow.CAR_GSM1 || '')],
      ['Tel2', sanitizeText(cari.CAR_TEL2 || activeRow.CAR_TEL2 || '')],
      ['Email', sanitizeText(cari.CAR_EMAIL || activeRow.CAR_EMAIL || '')],
      ['Ülke', sanitizeText(activeRow.ULKE_AD || '')],
      ['Son Değerlendirme', sanitizeText(activeRow.SON_DEG || '')],
      ['Son Durum', sanitizeText(activeRow.SON_DURUM || '')],
      ['Adres', sanitizeText((cari.CAR_ADRES || '').substring(0, 200))]
    ];
    doc.autoTable({
      startY: y + 4,
      head: [['Alan', 'Değer']],
      body: cariInfo,
      styles: { fontSize: 8, font: __fontLoaded ? FONT_NAME : undefined },
      headStyles: { fillColor: [60, 60, 60], textColor: 255 },
      margin: { left: margin, right: margin }
    });
    y = doc.lastAutoTable.finalY + 15;

    // Anemnez
    if (anemnezList.length) {
      y = addSectionHeader(doc, 'ANEMNEZ', y);
      const body = anemnezList.map(a => [sanitizeText(a.CA_ACK || '')]);
      doc.autoTable({
        startY: y + 4,
        head: [['Açıklama']],
        body,
        styles: { fontSize: 8, font: __fontLoaded ? FONT_NAME : undefined },
        headStyles: { fillColor: [80, 80, 80], textColor: 255 },
        margin: { left: margin, right: margin }
      });
      y = doc.lastAutoTable.finalY + 15;
    }

    // Aramalar
    if (aramaList.length) {
      y = addSectionHeader(doc, 'ARAMALAR', y);
      const body = aramaList.slice(0, 50).map(r => [
        r.CD_ARA_TAR ? new Date(r.CD_ARA_TAR).toLocaleDateString('tr-TR') : '',
        sanitizeText(r.CD_DEGERLENDIRME || ''),
        sanitizeText(r.CD_YETKILI || '')
      ]);
      doc.autoTable({
        startY: y + 4,
        head: [['Tarih', 'Değerlendirme', 'Yetkili']],
        body,
        styles: { fontSize: 8, font: __fontLoaded ? FONT_NAME : undefined },
        headStyles: { fillColor: [100, 100, 100], textColor: 255 },
        margin: { left: margin, right: margin }
      });
      y = doc.lastAutoTable.finalY + 15;
    }

    // Satış Kayıtları
    if (satisList.length) {
      y = addSectionHeader(doc, 'SATIŞ KAYITLARI', y);
      const body = satisList.map(s => [
        s.CAS_GIR_TAR ? new Date(s.CAS_GIR_TAR).toLocaleDateString('tr-TR') : '',
        (s.CAS_SATIS_TUT || s.CAS_SATIS_TUT_TAR || 0).toLocaleString('tr-TR'),
        sanitizeText(s.CAS_SATIS_TURU || '')
      ]);
      doc.autoTable({
        startY: y + 4,
        head: [['Tarih', 'Tutar', 'Tür']],
        body,
        styles: { fontSize: 8, font: __fontLoaded ? FONT_NAME : undefined },
        headStyles: { fillColor: [120, 120, 120], textColor: 255 },
        margin: { left: margin, right: margin }
      });
      y = doc.lastAutoTable.finalY + 15;
    }

    // Borç / Alacak
    if (paraList.length) {
      y = addSectionHeader(doc, 'BORÇ / Alınan', y);
      const body = paraList.map(p => [
        p.CP_TAR ? new Date(p.CP_TAR).toLocaleDateString('tr-TR') : '',
        sanitizeText(p.CP_TUR || ''),
        p.CP_BA ? 'Borç' : 'Alınan',
        (p.CP_TUTAR || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })
      ]);
      doc.autoTable({
        startY: y + 4,
        head: [['Tarih', 'Tür', 'Tip', 'Tutar']],
        body,
        styles: { fontSize: 8, font: __fontLoaded ? FONT_NAME : undefined },
        headStyles: { fillColor: [140, 140, 140], textColor: 255 },
        margin: { left: margin, right: margin }
      });
      y = doc.lastAutoTable.finalY + 10;
      const totalBorc = paraList.filter(p => p.CP_BA).reduce((s, p) => s + (p.CP_TUTAR || 0), 0);
      const totalAlacak = paraList.filter(p => !p.CP_BA).reduce((s, p) => s + (p.CP_TUTAR || 0), 0);
      doc.setFontSize(9);
      doc.text(`Toplam Borç: ${totalBorc.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} TL`, margin, y);
      y += 12;
      doc.text(`Toplam Alacak: ${totalAlacak.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} TL`, margin, y);
      y += 12;
      doc.text(`Net: ${(totalBorc - totalAlacak).toLocaleString('tr-TR', { minimumFractionDigits: 2 })} TL`, margin, y);
      y += 20;
    }

    // Resimler Grid (3 x 2 per page)
    if (resimList.length) {
      y = addSectionHeader(doc, 'RESİMLER', y);
      const imagesPerRow = 3;
      const imageSize = 70; // px
      const gap = 15;
      let col = 0;
      for (let i = 0; i < resimList.length; i++) {
        const r = resimList[i];
        const dataUrl = await fetchImageAsDataUrl(`${base}fileops/resDown?CR_REF=${r.CR_REF}`);
        if (dataUrl) {
          if (y + imageSize + gap > doc.internal.pageSize.getHeight() - 60) {
            doc.addPage();
            y = margin;
            y = addSectionHeader(doc, 'RESİMLER (devam)', y);
            col = 0;
          }
          const x = margin + col * (imageSize + gap);
          doc.addImage(dataUrl, 'JPEG', x, y, imageSize, imageSize);
          col++;
          if (col >= imagesPerRow) { col = 0; y += imageSize + gap; }
        }
        if (i >= 17) break; // limit
      }
      y += 10;
    }

    stampFooters(doc);
    const fileName = `Cari_${(cari.CAR_ADSOY || cari.CAR_REF || 'cari')}_${new Date().toISOString().slice(0,10)}.pdf`;
    doc.save(fileName);
    notify('PDF hazır', 'success', 2000);
  } catch (err) {
    console.error(err);
    notify('PDF oluşturulamadı', 'error', 3000);
  }
}
